import React, { createContext, useContext, ReactNode } from 'react';
import clsx from 'clsx';
import GenericButton, { ButtonType } from 'components/Button';
import { ReactComponent as ChevronIcon } from 'images/chevron.svg';

import styles from './ActionCard.module.scss';

export enum ActionCardVariant {
  Gradient = 'gradient',
  White = 'white',
  LightGreen = 'lightGreen',
  LightBlue = 'lightBlue',
  LightYellow = 'lightYellow',
}

export enum ActionCardSize {
  Default = 'default',
  Compact = 'compact',
}

interface BaseProps {
  children: ReactNode;
  className?: string;
}

interface ClickableProps extends BaseProps {
  onClick?: () => void;
  isLoading?: boolean;
}

interface ActionCardContextType {
  className?: string;
  variant: ActionCardVariant;
  size: ActionCardSize;
}

const ActionCardContext = createContext<ActionCardContextType | undefined>(undefined);

const useActionCardContext = () => {
  const context = useContext(ActionCardContext);
  if (!context) {
    throw new Error('ActionCard sub-components must be used within an ActionCard');
  }
  return context;
};

interface ActionCardProps extends BaseProps {
  variant?: ActionCardVariant;
  size?: ActionCardSize;
  showChevron?: boolean;
  onClick?: () => void;
  contentClassName?: string;
}

type ActionCardSubComponents = {
  Duration: typeof Description;
  Title: typeof Title;
  Description: typeof Description;
  Button: typeof Button;
  Row: typeof Row;
  Icon: typeof Icon;
  Col: typeof Col;
};

const ActionCard: React.FC<ActionCardProps> & ActionCardSubComponents = ({
  children,
  className,
  variant = ActionCardVariant.Gradient,
  size = ActionCardSize.Default,
  showChevron,
  onClick,
  contentClassName,
}) => {
  const contextValue = { className, variant, size };

  const cardClasses = clsx(
    styles.card,
    styles[`card--${variant}`],
    styles[`card--${size}`],
    onClick ? styles.clickable : '',
    className,
  );

  const contentClasses = clsx(styles.content, styles[`content--${size}`], contentClassName);

  return (
    <ActionCardContext.Provider value={contextValue}>
      <div className={cardClasses} onClick={onClick}>
        <div className={contentClasses}>{children}</div>
        {showChevron && <ChevronIcon className={styles.chevron} />}
      </div>
    </ActionCardContext.Provider>
  );
};

const Title: React.FC<BaseProps> = ({ children, className }) => {
  useActionCardContext();
  return <h3 className={clsx(styles.title, className)}>{children}</h3>;
};

const Description: React.FC<BaseProps> = ({ children, className }) => {
  useActionCardContext();
  return <p className={clsx(styles.description, className)}>{children}</p>;
};

const Button: React.FC<ClickableProps> = ({ children, onClick, className, isLoading }) => {
  useActionCardContext();
  return (
    <GenericButton
      className={clsx(styles.button, className)}
      onClick={onClick}
      type={ButtonType.Secondary}
      isLoading={isLoading}
      loaderSize={40}
    >
      {children}
    </GenericButton>
  );
};

const Row: React.FC<BaseProps> = ({ children, className }) => {
  useActionCardContext();
  return <div className={clsx(styles.row, className)}>{children}</div>;
};

const Col: React.FC<BaseProps> = ({ children, className }) => {
  useActionCardContext();
  return <div className={clsx(styles.col, className)}>{children}</div>;
};

const Icon: React.FC<BaseProps> = ({ children, className }) => {
  useActionCardContext();
  return <div className={clsx(styles.icon, className)}>{children}</div>;
};

ActionCard.Duration = Description;
ActionCard.Title = Title;
ActionCard.Description = Description;
ActionCard.Button = Button;
ActionCard.Row = Row;
ActionCard.Icon = Icon;
ActionCard.Col = Col;
export default ActionCard;
